import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Turnstile from "react-turnstile"; // 1. Add this
import { toast } from "react-hot-toast";

const BookForm = ({ type = "donation", onSubmit }) => {
  const donationFields = {
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    address: "",
    city: "",
    pincode: "",
  };

  const suggestionFields = {
    title: "",
    author: "",
    edition: "",
     bookLanguage: "",
    message: "",
  };

  const [fields, setFields] = useState(
    type === "donation" ? donationFields : suggestionFields
  );
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(""); // 2. Add CAPTCHA state

  useEffect(() => {
    setFields(type === "donation" ? donationFields : suggestionFields);
    setErrors({});
    setCaptchaToken(""); // Reset CAPTCHA when form type changes
  }, [type]);

  const labels = {
    firstName: "First name",
    lastName: "Last name",
    email: "Email Address",
    contact: "Mobile Number",
    address: "Address",
    city: "City",
    pincode: "Pincode",
    userId: "User ID",
    title: "Title of Book",
    author: "Author",
    edition: "Edition",
    bookLanguage: "Book Language",
    message: type === "donation" ? "Notes" : "Message",
  };

  const isRequired = (name) => {
    if (type === "donation") return true; // donation: all required (unchanged)
    // suggestion: only title and author required
    return name === "title" || name === "author" || name === "bookLanguage";
  };

  const hasValidationErrors = Object.values(errors).some(
    (e) => e && e.length > 0
  );
  const requiredFields = Object.keys(fields).filter((k) => isRequired(k));
  const requiredMissing = requiredFields.some((f) => !fields[f].trim());
  const submitDisabled =
    hasValidationErrors || requiredMissing || !captchaToken;

  const typeToImage = {
    donation: "/images/book donation img.png",
    suggestion: "/images/suggest book img.jpg",
  };

  const validateSingleField = (name, value) => {
    let message = "";

    if (isRequired(name) && !value.trim()) {
      message = `${labels[name]} is required`;
    } else {
      // if user provided a value (even for optional fields), enforce format checks
      if (value.trim()) {
        if (
          name === "email" &&
          !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)
        ) {
          message = "Invalid email format";
        }
        if (name === "contact" && !/^\d{10}$/.test(value)) {
          message = "Mobile number must be 10 digits";
        }
        if (name === "pincode" && !/^\d{6}$/.test(value)) {
          message = "Pincode must be 6 digits";
        }
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: message,
    }));
  };

  const validateFields = () => {
    const newErrors = {};

    for (const [key, value] of Object.entries(fields)) {
      // presence check only for required fields
      if (isRequired(key) && !value.trim()) {
        newErrors[key] = `${labels[key]} is required`;
        continue;
      }

      // if value exists (even for optional fields), validate format
      if (value.trim()) {
        if (key === "email" && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)) {
          newErrors[key] = "Invalid email format";
        }
        if (key === "contact" && !/^\d{10}$/.test(value)) {
          newErrors[key] = "Mobile number must be 10 digits";
        }
        if (key === "pincode" && !/^\d{6}$/.test(value)) {
          newErrors[key] = "Pincode must be 6 digits";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateSingleField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      toast.error("Please complete the CAPTCHA.");
      return;
    }

    if (validateFields()) {
      try {
        if (onSubmit) {
          await onSubmit({ ...fields });
          toast.success("Form submitted successfully!");
          setFields(type === "donation" ? donationFields : suggestionFields);
          setCaptchaToken(""); // Reset CAPTCHA
        } else {
          console.log({ ...fields, captchaToken });
          toast.success("Form submitted !");
        }
      } catch (error) {
        if (error && error.message === "Login cancelled") {
          // Do nothing — user intentionally cancelled to login.
          return;
        }
        // Otherwise show the real error toast
        toast.error("Something went wrong. Please try again.");
        console.error(error);
      }
    } else {
      toast.error("Oops! Looks like a few things are missing or incorrect.");
    }
  };

  return (
    <div className="w-full bg-white">
      <div className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] lg:h-[380px] overflow-hidden">
        <img
          src={typeToImage[type] || "/images/default-book-image.png"}
          alt="Bookshelf"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>

      <div className="relative -top-48 sm:-top-56 md:-top-64 lg:-top-72 flex justify-center px-2 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-xs sm:max-w-lg md:max-w-xl lg:max-w-2xl bg-[#fff8f5] border border-orange-300 rounded-xl p-3 sm:p-4 md:p-6 shadow-xl"
        >
          <h2 className="text-center text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-1 sm:mb-2">
            Book {type === "donation" ? "Donation" : "Suggestion"} Form
          </h2>
          <div className="w-16 sm:w-20 md:w-24 h-1 mx-auto bg-orange-500 rounded-full mb-3 sm:mb-4 relative">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-orange-600 text-xs sm:text-sm font-light">
              ✦
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
            {type === "donation" ? (
              // Donation form layout (existing)
              Object.keys(fields).map((key) => {
                if (key === "city") {
                  return (
                    <div
                      key="city-pincode"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3"
                    >
                      {["city", "pincode"].map((field) => (
                        <div key={field}>
                          <label
                            htmlFor={field}
                            className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                          >
                            {labels[field]}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            name={field}
                            type="text"
                            value={fields[field]}
                            onChange={handleChange}
                            className={`w-full border ${
                              errors[field]
                                ? "border-red-500"
                                : "border-orange-300"
                            } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                            placeholder={`Enter your ${labels[
                              field
                            ].toLowerCase()}`}
                          />
                          {errors[field] && (
                            <p className="text-red-500 text-xs sm:text-sm mt-1">
                              {errors[field]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }

                if (key === "pincode") return null;

                return (
                  <div key={key}>
                    <label
                      htmlFor={key}
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      {labels[key]}{" "}
                      {isRequired(key) ? (
                        <span className="text-red-500">*</span>
                      ) : (
                        <span className="text-gray-400 text-xs ml-1">
                          (optional)
                        </span>
                      )}
                    </label>
                    {key === "message" ? (
                      <textarea
                        name={key}
                        value={fields[key]}
                        onChange={handleChange}
                        rows={2}
                        className={`w-full border ${
                          errors[key] ? "border-red-500" : "border-orange-300"
                        } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                        placeholder={`Enter your ${labels[key].toLowerCase()}`}
                      />
                    ) : (
                      <input
                        name={key}
                        type={key === "email" ? "email" : "text"}
                        value={fields[key]}
                        onChange={handleChange}
                        className={`w-full border ${
                          errors[key] ? "border-red-500" : "border-orange-300"
                        } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                        placeholder={`Enter your ${labels[key].toLowerCase()}`}
                      />
                    )}
                    {errors[key] && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors[key]}</p>
                    )}
                  </div>
                );
              })
            ) : (
              // Suggestion form layout with two columns
              <>
                {/* Title and Author in one row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {["title", "author"].map((field) => (
                    <div key={field}>
                      <label
                        htmlFor={field}
                        className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                      >
                        {labels[field]}{" "}
                        {isRequired(field) ? (
                          <span className="text-red-500">*</span>
                        ) : (
                          <span className="text-gray-400 text-xs ml-1">
                            (optional)
                          </span>
                        )}
                      </label>
                      <input
                        name={field}
                        type="text"
                        value={fields[field]}
                        onChange={handleChange}
                        className={`w-full border ${
                          errors[field]
                            ? "border-red-500"
                            : "border-orange-300"
                        } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                        placeholder={`Enter ${labels[field].toLowerCase()}`}
                      />
                      {errors[field] && (
                        <p className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[field]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Edition and Book Language in one row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {["edition", "bookLanguage"].map((field) => (
                    <div key={field}>
                      <label
                        htmlFor={field}
                        className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                      >
                        {labels[field]}{" "}
                        {isRequired(field) ? (
                          <span className="text-red-500">*</span>
                        ) : (
                          <span className="text-gray-400 text-xs ml-1">
                            (optional)
                          </span>
                        )}
                      </label>
                      <input
                        name={field}
                        type="text"
                        value={fields[field]}
                        onChange={handleChange}
                        className={`w-full border ${
                          errors[field]
                            ? "border-red-500"
                            : "border-orange-300"
                        } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                        placeholder={`Enter ${labels[field].toLowerCase()}`}
                      />
                      {errors[field] && (
                        <p className="text-red-500 text-xs sm:text-sm mt-1">
                          {errors[field]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Message field (full width) */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >
                    {labels.message}{" "}
                    {isRequired("message") ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="text-gray-400 text-xs ml-1">
                        (optional)
                      </span>
                    )}
                  </label>
                  <textarea
                    name="message"
                    value={fields.message}
                    onChange={handleChange}
                    rows={2}
                    className={`w-full border ${
                      errors.message ? "border-red-500" : "border-orange-300"
                    } rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-400`}
                    placeholder={`Enter your ${labels.message.toLowerCase()}`}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* CAPTCHA Here */}
            <div className="flex justify-center mt-3">
              <div className="transform scale-75 sm:scale-90 md:scale-100">
                <Turnstile
                  sitekey="0x4AAAAAABciclZPace9AkOm" // Replace this
                  onVerify={setCaptchaToken}
                />
              </div>
            </div>

            <div className="flex justify-center mt-3">
              <button
                type="submit"
                className="w-32 sm:w-36 md:w-40 bg-orange-500 text-white font-semibold py-2 sm:py-2.5 px-4 sm:px-6 rounded-md hover:bg-orange-600 transition shadow-sm disabled:opacity-50 text-sm sm:text-base"
                disabled={submitDisabled}
              >
                Submit
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default BookForm;
